'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { type ComponentProps, type MouseEventHandler, useState } from 'react';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import useIsActivePath from './hooks/useIsActivePath';
import InputSelect from './InputSelect';
import useHash from './hooks/useHash';
import { useElementFitsContainer } from './hooks/useElementFitsContainer';
import { cn } from '~/utils';
import { searchParamStringOfKeys, UrlPath } from '~/utils/routing/shared';
import assert from 'assert';
export type Tab = {
  name: string;
  href: string;
  isActive?: boolean | ((href: Tab['href']) => boolean);
  errors?: number;
  isDisabled?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};
function TabItem(t0) {
  const $ = _c(12);
  const {
    name,
    href,
    onClick,
    isActive,
    isDisabled,
    errors
  } = t0;
  const isCurrent = useIsActivePath(href);
  const [currentHash] = useHash();
  const isCurrentHash = href.startsWith("#") ? currentHash === href.substring(1) : false;
  const isActiveHref = isActive === undefined ? isCurrentHash || isCurrent : typeof isActive === "function" ? isActive(href) : isActive === true;
  const t1 = isActiveHref ? "border-blue-200 bg-white text-secondary hover:border-secondary" : "border-transparent text-primary hover:text-secondary";
  const t2 = isDisabled ? "pointer-events-none opacity-50" : "";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("border", t1, "relative rounded-4xl px-3 py-2 align-middle text-sm font-medium sm:px-6 sm:py-5", t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isActiveHref ? "page" : undefined;
  let t5;
  if ($[3] !== errors) {
    t5 = errors && errors > 0 ? <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-orange text-xs leading-none text-white"><span className="leading-none">{errors}</span></span> : null;
    $[3] = errors;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== href || $[6] !== name || $[7] !== onClick || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <Link href={href} onClick={onClick} className={t3} aria-current={t4}>{name}{t5}</Link>;
    $[5] = href;
    $[6] = name;
    $[7] = onClick;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
type HorizontalTabsProps = {
  tabs: Array<Tab> | Readonly<Array<Tab>>;
  className?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  /** @default normal */
};
export default function HorizontalTabs(t0) {
  const $ = _c(15);
  const {
    tabs,
    onClick,
    className
  } = t0;
  const router = useRouter();
  const location = usePathname();
  const params = useSearchParams();
  assert(params, "HorizontalTabs >params is required");
  let t1;
  if ($[0] !== params) {
    const includeSearchKeys = ["view"];
    t1 = searchParamStringOfKeys(params, includeSearchKeys);
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchParamsString = t1;
  const currentPathWithView = `${location}${searchParamsString ? `?${searchParamsString}` : ""}`;
  const [container, element, fitsContainer] = useElementFitsContainer();
  let t2;
  if ($[2] !== className) {
    t2 = cx("flex flex-1", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentPathWithView || $[5] !== element || $[6] !== fitsContainer || $[7] !== onClick || $[8] !== router || $[9] !== tabs) {
    t3 = fitsContainer ? <nav ref={element} className="inline-flex whitespace-nowrap rounded-4xl bg-brown-100" aria-label="Tabs">{tabs.map(tab => <TabItem key={tab.name} {...tab} onClick={onClick} />)}</nav> : <nav><InputSelect onChange={target => router.push(target)} value={currentPathWithView} options={tabs} valueField="href" labelField="name" /></nav>;
    $[4] = currentPathWithView;
    $[5] = element;
    $[6] = fitsContainer;
    $[7] = onClick;
    $[8] = router;
    $[9] = tabs;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== container || $[12] !== t2 || $[13] !== t3) {
    t4 = <div ref={container} className={t2}>{t3}</div>;
    $[11] = container;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
export namespace HorizontalStatelessTabs {
  export type TabConfig<T extends string> = Array<{
    label: string;
    value: T;
    disabled?: boolean;
    disabledTooltip?: string;
    errors?: number;
  }>;
  export type inferTabs<T extends TabConfig<any>> = T extends TabConfig<infer U> ? U : never;
  export type Props<T extends string> = {
    className?: string;
    tabs: HorizontalStatelessTabs.TabConfig<T>;
    onChange: (value: T) => void;
    value: T & {};
    /** @default 'fit' */
    layout?: 'fit' | 'fill';
    /** @default 'normal' */
    size?: 'normal' | 'small';
    /** Custom render function for tab content. When provided, replaces the default label rendering. */
    renderTab?: (opts: {
      tab: HorizontalStatelessTabs.TabConfig<T>[number];
      isActive: boolean;
    }) => React.ReactNode;
  };
}
export function HorizontalStatelessTabs(props) {
  const $ = _c(15);
  const size = props.size ?? "normal";
  let t0;
  if ($[0] !== props || $[1] !== size) {
    t0 = !props.renderTab && size === "normal" && <div className={cn("sm:hidden", props.className, "bg-transparent!")}><InputSelect options={props.tabs} onChange={value => {
        const selectedTab = props.tabs.find(tab => tab.value === value);
        if (selectedTab) {
          props.onChange(selectedTab.value);
        }
      }} value={props.value} valueField="value" labelField="label" /></div>;
    $[0] = props;
    $[1] = size;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = props.renderTab || size === "small" ? "inline-flex" : "hidden sm:inline-flex";
  let t2;
  if ($[3] !== props.className || $[4] !== t1) {
    t2 = cx(t1, "w-fit bg-brown-50", "rounded-4xl", props.className);
    $[3] = props.className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== size) {
    t3 = props.tabs.map(tab_0 => {
      const isActive = tab_0.value === props.value;
      const isDisabled = tab_0.disabled;
      return <button key={tab_0.value} type="button" data-testid={`tab-${tab_0.value}`} onClick={() => !isDisabled && props.onChange(tab_0.value)} className={cx("border", isActive ? "border-blue-200 bg-white text-secondary hover:border-secondary" : "border-transparent text-primary hover:text-secondary", "relative flex items-center gap-2 text-sm font-medium transition-all", props.layout === "fill" && "flex-1 justify-center", isDisabled ? "pointer-events-none opacity-50" : "", "rounded-4xl", size === "normal" ? "p-3 sm:px-6 sm:py-5" : "p-3")} aria-current={isActive ? "page" : undefined} aria-disabled={isDisabled} title={isDisabled && tab_0.disabledTooltip ? tab_0.disabledTooltip : undefined}>{props.renderTab ? props.renderTab({
          tab: tab_0,
          isActive
        }) : tab_0.label}{!props.renderTab && tab_0.errors && tab_0.errors > 0 ? <span className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-orange text-xs leading-none text-white"><span className="leading-none">{tab_0.errors}</span></span> : null}</button>;
    });
    $[6] = props;
    $[7] = size;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <nav className={t2} aria-label="Tabs">{t3}</nav>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t0 || $[13] !== t4) {
    t5 = <>{t0}{t4}</>;
    $[12] = t0;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
HorizontalTabs.StateLess = HorizontalStatelessTabs;

/** This should be given to `HorizontalTabs.StateLess` */
HorizontalTabs.useState = <T extends string,>(tabs: HorizontalStatelessTabs.Props<T>['tabs'], initialValue?: HorizontalStatelessTabs.Props<T>['value']) => {
  if (tabs.length === 0) throw new Error('Need at least 1 tab to be provided');

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [currentTab, setCurrentTab] = useState(initialValue ?? tabs?.[0]?.value!);
  return {
    tabs,
    value: currentTab,
    onChange: setCurrentTab
  } as const satisfies ComponentProps<typeof HorizontalStatelessTabs<T>>;
};
export const HorizontalTabs_ByPathname: React.FC<{
  tabs: HorizontalStatelessTabs.TabConfig<string>;
}> = props => {
  const $ = _c(5);
  const path = new UrlPath(usePathname());
  const router = useRouter();
  const T0 = HorizontalStatelessTabs;
  const t0 = props.tabs;
  const t1 = path.getLastSegment();
  const t2 = tab => router.push(path.copy().setLastSegment(tab).toString());
  let t3;
  if ($[0] !== T0 || $[1] !== props.tabs || $[2] !== t1 || $[3] !== t2) {
    t3 = <T0 tabs={t0} value={t1} onChange={t2} />;
    $[0] = T0;
    $[1] = props.tabs;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};